import React, { useEffect, useState, Children } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { motion } from 'framer-motion';
// ── RESPONSIVE HOOK ───────────────────────────────────────────────────────────
// El app está estilizado con objetos `style` en línea, por lo que las clases
// de breakpoints de Tailwind no aplican. Este hook permite ramificar estilos
// según el ancho de pantalla (móvil ≤ 640px).
export function useIsMobile(maxWidth = 640): boolean {
  const query = `(max-width: ${maxWidth}px)`;
  const get = () => typeof window !== 'undefined' && window.matchMedia(query).matches;
  const [isMobile, setIsMobile] = useState<boolean>(get);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setIsMobile(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return isMobile;
}
// ── DESIGN TOKENS ─────────────────────────────────────────────────────────────
export const T = {
  brand: '#FF6B01',
  // signature electric energy accent (teal-lime), used sparingly for emphasis
  accent: '#FF6B01',
  accentSoft: 'rgba(255,107,1,.12)',
  accentBorder: 'rgba(255,107,1,.32)',
  text: '#1A1207',
  textSecondary: 'rgba(26,18,7,.6)',
  textMuted: 'rgba(26,18,7,.4)',
  surface: 'rgba(0,0,0,0.02)',
  surfaceSubtle: 'rgba(0,0,0,0.04)',
  surfaceHover: 'rgba(0,0,0,0.06)',
  border: 'rgba(0,0,0,0.08)',
  borderStrong: 'rgba(0,0,0,0.18)',
  shadow: '0 8px 32px rgba(0,0,0,.08)',
  shadowSoft: '0 12px 40px rgba(0,0,0,.05)',
  innerHi: 'inset 0 1px 0 rgba(255,255,255,.6)',
  rLg: 16,
  rMd: 12,
  rSm: 8,
  ok: '#16a34a',
  okSoft: 'rgba(22,163,74,.12)',
  warn: '#d97706',
  warnSoft: 'rgba(217,119,6,.12)',
  danger: '#dc2626',
  dangerSoft: 'rgba(220,38,38,.12)',
  info: '#2563eb',
  infoSoft: 'rgba(37,99,235,.12)'
};
// ── LUX STATUS HELPER ─────────────────────────────────────────────────────────
export function luxEstado(lux: number, min: number): {
  label: string;
  color: string;
  soft: string;
} {
  if (lux < min) return {
    label: 'Fuera de rango',
    color: T.danger,
    soft: T.dangerSoft
  };
  if (lux < min * 1.2) return {
    label: 'Al límite',
    color: T.warn,
    soft: T.warnSoft
  };
  return {
    label: 'En rango',
    color: T.ok,
    soft: T.okSoft
  };
}
// ── TOKENS ────────────────────────────────────────────────────────────────────
export const glass: CSSProperties = {
  background: 'rgba(255,255,255,0.85)',
  backdropFilter: 'blur(28px) saturate(180%)',
  WebkitBackdropFilter: 'blur(28px) saturate(180%)',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 20,
  boxShadow: T.shadowSoft
};
export const glassLight: CSSProperties = {
  background: 'rgba(255,255,255,0.6)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 16,
  boxShadow: T.shadowSoft
};
export const inputStyle: CSSProperties = {
  width: '100%',
  padding: '11px 14px',
  background: 'rgba(0,0,0,0.03)',
  border: '1px solid rgba(0,0,0,0.1)',
  borderRadius: 12,
  fontSize: 14,
  color: '#1A1207',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color .2s, box-shadow .2s',
  boxSizing: 'border-box'
};
export const primaryButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  whiteSpace: 'nowrap',
  fontFamily: 'inherit',
  padding: '10px 20px',
  border: '1px solid rgba(255,107,1,.4)',
  borderRadius: 12,
  background: 'linear-gradient(135deg, #FF8C00 0%, #FF5A00 100%)',
  color: '#ffffff',
  fontWeight: 800,
  fontSize: 13,
  cursor: 'pointer',
  letterSpacing: '0.01em',
  boxShadow: '0 4px 22px rgba(255,107,1,0.3), inset 0 1px 0 rgba(255,255,255,.25)'
};
export const ghostButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  whiteSpace: 'nowrap',
  padding: '8px 14px',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 10,
  background: 'rgba(0,0,0,0.04)',
  color: 'rgba(26,18,7,0.78)',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'background .15s, border-color .15s'
};
export const dangerButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  whiteSpace: 'nowrap',
  padding: '8px 14px',
  border: '1px solid rgba(220,38,38,0.3)',
  borderRadius: 10,
  background: 'rgba(220,38,38,0.1)',
  color: '#dc2626',
  fontWeight: 700,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'background .15s, border-color .15s'
};
// ── SHARED STYLE INJECTION ──────────────────────────────────────────────────
const GETR_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap');
  .getr-page * { font-family: 'DM Sans', sans-serif; }
  .getr-page h1, .getr-page .title { font-family: 'Space Grotesk', sans-serif; }
  .getr-btn-ghost:hover { background: rgba(0,0,0,.08) !important; border-color: rgba(0,0,0,.15) !important; }
  .getr-card:hover { transform: translateY(-3px); border-color: rgba(255,107,1,.26) !important; box-shadow: 0 16px 44px rgba(0,0,0,.08), inset 0 1px 0 rgba(255,255,255,.6) !important; }
  .getr-card { transition: transform .22s cubic-bezier(.2,.8,.2,1), border-color .22s, box-shadow .22s; }
  .getr-row:hover { background: rgba(0,0,0,.03) !important; }
  .getr-input:focus { border-color: rgba(255,107,1,.6) !important; box-shadow: 0 0 0 3px rgba(255,107,1,.14); }
  .getr-primary:hover button { filter: brightness(1.06); transform: translateY(-1px); box-shadow: 0 8px 30px rgba(255,107,1,.4), inset 0 1px 0 rgba(255,255,255,.3) !important; }
  .getr-primary button { transition: all .18s; }

  /* ── Forzar texto legible en TODOS los campos (inputs, selects, fecha) ── */
  .getr-page input,
  .getr-page select,
  .getr-page textarea {
    color: #1A1207 !important;
    color-scheme: light;
  }
  .getr-page input::placeholder,
  .getr-page textarea::placeholder {
    color: rgba(26,18,7,.45) !important;
  }
  /* Opciones del <select> (el navegador las pinta con su propio fondo claro) */
  .getr-page select option {
    color: #1A1207 !important;
    background: #ffffff !important;
  }
  /* Input de fecha: el ícono de calendario y el texto nativo */
  .getr-page input[type="date"],
  .getr-page input[type="time"],
  .getr-page input[type="datetime-local"] {
    color: #1A1207 !important;
  }
  .getr-page input[type="date"]::-webkit-calendar-picker-indicator,
  .getr-page input[type="time"]::-webkit-calendar-picker-indicator,
  .getr-page input[type="datetime-local"]::-webkit-calendar-picker-indicator {
    cursor: pointer;
  }
  /* Autofill del navegador no debe pintar fondo claro con texto oscuro */
  .getr-page input:-webkit-autofill {
    -webkit-text-fill-color: #1A1207 !important;
    -webkit-box-shadow: 0 0 0 1000px rgba(0,0,0,0.03) inset !important;
    caret-color: #1A1207;
  }
`;
// ── PAGE SHELL ────────────────────────────────────────────────────────────────
export function PageShell({
  title,
  sub,
  action,
  children
}: {
  title: string;
  sub: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  const isMobile = useIsMobile();
  return <>
      <style data-id="element-1176" data-mp="8li8uh60f07p">{`{GETR_STYLES}`}</style>
      <div className="getr-page" data-id="element-1177" data-mp="3h0a2lu9yq0r7">
        <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        ease: [0.2, 0.8, 0.2, 1]
      }} style={{
        marginBottom: 30,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: 16,
        flexWrap: 'wrap'
      }} data-id="element-1178" data-mp="3h1bg05egazc3">
          
          <div style={{
          position: 'relative'
        }} data-id="element-1179" data-mp="1675uq84ua04z">
            
            <div style={{
            fontSize: 10,
            fontWeight: 700,
            color: T.accent,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            marginBottom: 9,
            display: 'flex',
            alignItems: 'center',
            gap: 7
          }} data-id="element-1180" data-mp="11a76u7brqpf8">
              
              <span style={{
              width: 16,
              height: 2,
              borderRadius: 2,
              background: T.accent,
              boxShadow: `0 0 10px ${T.accent}`
            }} data-id="element-1181" data-mp="34ops4uuzca80" />
              
              Junquito Gas
            </div>
            <div className="title" style={{
            fontSize: isMobile ? 22 : 28,
            fontWeight: 800,
            color: '#1A1207',
            letterSpacing: '-0.03em',
            fontFamily: "'Space Grotesk', sans-serif",
            lineHeight: 1.08
          }} data-id="element-1182" data-mp="3igxmkfysjp3j">
              
              {title}
            </div>
            <div style={{
            fontSize: 13,
            color: T.textSecondary,
            marginTop: 7,
            fontWeight: 500
          }} data-id="element-1183" data-mp="1t4lrqqs1qfi1">
              
              {sub}
            </div>
          </div>
          {action && <div className="getr-primary" data-id="element-1184" data-mp="1lrwcxtw4q80d">{action}</div>}
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.06,
        ease: [0.2, 0.8, 0.2, 1]
      }} style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14
      }} data-id="element-1185" data-mp="1lpjnkvo1kvda">
          
          {children}
        </motion.div>
      </div>
    </>;
}
// ── MOTION GRID (staggered entrance for card grids) ───────────────────────────
export function MotionGrid({
  children,
  style
}: {
  children: ReactNode;
  style?: CSSProperties;
}) {
  return <motion.div style={style} initial="hidden" animate="show" variants={{
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.05
      }
    }
  }} data-id="element-1186" data-mp="1ujoj3se4euz5">
      
      {children}
    </motion.div>;
}
export function MotionItem({
  children,
  style,
  className
}: {
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
}) {
  return <motion.div className={className} style={style} variants={{
    hidden: {
      opacity: 0,
      y: 14
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.34,
        ease: [0.2, 0.8, 0.2, 1]
      }
    }
  }} data-id="element-1187" data-mp="3u3ls9gxk1ayb">
      
      {children}
    </motion.div>;
}
// ── BADGE ─────────────────────────────────────────────────────────────────────
export function Badge({
  label,
  color,
  soft,
  dot = true
}: {
  label: string;
  color: string;
  soft: string;
  dot?: boolean;
}) {
  return <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    fontWeight: 700,
    color,
    background: soft,
    border: `1px solid ${color}33`,
    padding: '4px 11px',
    borderRadius: 99,
    letterSpacing: '0.01em'
  }} data-id="element-1188" data-mp="vk5cul13lfm1">
      
      {dot && <span style={{
      width: 6,
      height: 6,
      borderRadius: '50%',
      background: color,
      boxShadow: `0 0 7px ${color}`
    }} data-id="element-1189" data-mp="2se9voyesqzbk" />}
      {label}
    </span>;
}
// ── EMPTY STATE ───────────────────────────────────────────────────────────────
export function EmptyState({
  title,
  hint,
  accent = T.accent,
  icon
}: {
  title: string;
  hint?: string;
  accent?: string;
  icon?: ReactNode;
}) {
  return <div style={{
    ...glassLight,
    borderStyle: 'dashed',
    borderColor: 'rgba(0,0,0,.12)',
    padding: '40px 24px',
    textAlign: 'center'
  }} data-id="element-1190" data-mp="133eokkx2tqe9">
      
      <div style={{
      width: 52,
      height: 52,
      margin: '0 auto 14px',
      borderRadius: 14,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `${accent}1f`,
      border: `1px solid ${accent}3a`,
      color: accent
    }} data-id="element-1191" data-mp="u4kkyalbvzr7">
        
        {icon ?? <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" data-id="element-1192" data-mp="29kjp7artrbm6">
          
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" data-id="element-1193" data-mp="2d2pjwku0rkbl" />
            <polyline points="3.27 6.96 12 12.01 20.73 6.96" data-id="element-1194" data-mp="282kzijpzq5g3" />
          </svg>}
      </div>
      <div style={{
      fontWeight: 700,
      color: T.text,
      fontSize: 15,
      marginBottom: 5
    }} data-id="element-1195" data-mp="233roqz238sjm">
        
        {title}
      </div>
      {hint && <div style={{
      color: T.textSecondary,
      fontSize: 13,
      maxWidth: 340,
      margin: '0 auto'
    }} data-id="element-1196" data-mp="1z5aql50nasec">
        
          {hint}
        </div>}
    </div>;
}
// ── SECTION TITLE ─────────────────────────────────────────────────────────────
export function SectionTitle({
  children
}: {
  children: ReactNode;
}) {
  return <div style={{
    fontSize: 11,
    fontWeight: 700,
    color: T.textMuted,
    marginBottom: 14,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    gap: 8
  }} data-id="element-1197" data-mp="2i2lp2negdhsq">
      
      <span style={{
      width: 5,
      height: 5,
      borderRadius: '50%',
      background: T.accent
    }} data-id="element-1198" data-mp="39rotc36cas5d" />
      

      {children}
    </div>;
}
// ── STAT CARD ─────────────────────────────────────────────────────────────────
export function StatCard({
  label,
  value,
  note,
  accent = T.accent
}: {
  label: string;
  value: string;
  note: string;
  accent?: string;
}) {
  const isMobile = useIsMobile();
  return <div className="getr-card" style={{
    ...glassLight,
    padding: isMobile ? '16px 16px' : '22px 24px',
    position: 'relative',
    overflow: 'hidden'
  }} data-id="element-1199" data-mp="1a7qs00xu8ri8">
      
      {/* glow orb */}
      <div style={{
      position: 'absolute',
      right: -28,
      bottom: -28,
      width: 110,
      height: 110,
      borderRadius: '50%',
      background: `radial-gradient(circle, ${accent}2e 0%, transparent 70%)`,
      pointerEvents: 'none'
    }} data-id="element-1200" data-mp="1k66j43wv80rq" />
      

      {/* accent bar */}
      <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 2,
      background: `linear-gradient(90deg, ${accent}, ${accent}00)`,
      borderRadius: '16px 16px 0 0'
    }} data-id="element-1201" data-mp="1xxl5mkpzek3j" />
      

      <div style={{
      fontSize: 10,
      color: accent,
      fontWeight: 700,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      marginBottom: 12,
      display: 'flex',
      alignItems: 'center',
      gap: 6
    }} data-id="element-1202" data-mp="1kd8ilprpjcww">
        
        <span style={{
        width: 5,
        height: 5,
        borderRadius: '50%',
        background: accent,
        boxShadow: `0 0 8px ${accent}`
      }} data-id="element-1203" data-mp="1u3k0kg887mms" />
        

        {label}
      </div>
      <div style={{
      fontSize: isMobile ? 24 : 36,
      color: T.text,
      fontWeight: 800,
      letterSpacing: '-0.04em',
      fontFamily: "'Space Grotesk', sans-serif",
      lineHeight: 1.05,
      marginBottom: 9,
      wordBreak: 'break-word'
    }} data-id="element-1204" data-mp="11fxpyo0kzhqf">
        
        {value}
      </div>
      <div style={{
      fontSize: 12,
      color: T.textMuted,
      fontWeight: 500
    }} data-id="element-1205" data-mp="2rsn4niw10bpp">
        
        {note}
      </div>
    </div>;
}
// ── INFO CARD ─────────────────────────────────────────────────────────────────
export function InfoCard({
  title,
  value,
  note
}: {
  title: string;
  value: string;
  note: string;
}) {
  return <div className="getr-card" style={{
    ...glassLight,
    padding: 22,
    position: 'relative',
    overflow: 'hidden'
  }} data-id="element-1206" data-mp="2lasayslhve42">
      
      <div style={{
      fontSize: 11,
      color: T.textMuted,
      fontWeight: 700,
      letterSpacing: '0.04em',
      marginBottom: 10
    }} data-id="element-1207" data-mp="2pcdvn049swot">
        
        {title}
      </div>
      <div style={{
      fontSize: 28,
      color: T.text,
      fontWeight: 800,
      letterSpacing: '-0.03em',
      fontFamily: "'Space Grotesk', sans-serif",
      marginBottom: 5
    }} data-id="element-1208" data-mp="3hzhvkqoiaktq">
        
        {value}
      </div>
      <div style={{
      fontSize: 12,
      color: T.textSecondary,
      lineHeight: 1.4
    }} data-id="element-1209" data-mp="388n6lejvl74l">
        
        {note}
      </div>
    </div>;
}
// ── DATA GRID ─────────────────────────────────────────────────────────────────
export function DataGrid({
  headers,
  rows
}: {
  headers: string[];
  rows: string[][];
}) {
  return <div style={{
    ...glassLight,
    overflow: 'hidden'
  }} data-id="element-1210" data-mp="agb7dny154qr">
      
      <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${headers.length},1fr)`,
      background: T.surfaceSubtle,
      borderBottom: `1px solid ${T.border}`
    }} data-id="element-1211" data-mp="adxd3hb6bwk9">
        
        {headers.map(h => <div key={h} style={{
        padding: '13px 16px',
        fontSize: 10,
        color: T.textMuted,
        fontWeight: 700,
        letterSpacing: '.1em',
        textTransform: 'uppercase'
      }} data-id="element-1212" data-mp="3ro58vyq1cikf">
          
            {h}
          </div>)}
      </div>
      {rows.map((r, i) => <div key={i} className="getr-row" style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${headers.length},1fr)`,
      background: i % 2 === 1 ? T.surface : 'transparent',
      borderBottom: i < rows.length - 1 ? `1px solid ${T.border}` : 'none',
      transition: 'background .15s'
    }} data-id="element-1213" data-mp="3ith2244tasti" data-mpi={i}>
        
          {r.map((c, j) => <div key={`${i}-${j}`} style={{
        padding: '14px 16px',
        fontSize: 13,
        color: j === 0 ? T.text : T.textSecondary,
        fontWeight: j === 0 ? 600 : 400
      }} data-id="element-1214" data-mp="2tvfaqdejwvu8" data-mpi={j}>
          
              {c}
            </div>)}
        </div>)}
      {rows.length === 0 && <div style={{
      padding: '28px 20px',
      textAlign: 'center',
      color: T.textMuted,
      fontSize: 13
    }} data-id="element-1215" data-mp="38rmujcm1rv8z">
        
          Sin registros
        </div>}
    </div>;
}
// ── ACTION LIST ───────────────────────────────────────────────────────────────
export function ActionList({
  items
}: {
  items: {
    label: string;
    onClick: () => void;
  }[];
}) {
  return <div style={{
    ...glassLight,
    padding: '6px 4px'
  }} data-id="element-1216" data-mp="2l0963hr0ojta">
      
      {items.map(item => <button key={item.label} onClick={item.onClick} className="getr-btn-ghost" style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '13px 14px',
      background: 'transparent',
      border: 'none',
      borderBottom: `1px solid ${T.border}`,
      color: T.textSecondary,
      fontSize: 13,
      fontWeight: 600,
      textAlign: 'left',
      cursor: 'pointer',
      borderRadius: 10,
      transition: 'background .15s'
    }} data-id="element-1217" data-mp="2pv59fe1petlp">
        
          <span data-id="element-1218" data-mp="3giki6zipmr0m">{item.label}</span>
          <span style={{
        color: T.accent,
        fontSize: 16
      }} data-id="element-1219" data-mp="3ghj7wx9iq8jy">
          
            ›
          </span>
        </button>)}
    </div>;
}
// ── TOGGLE ────────────────────────────────────────────────────────────────────
export function Toggle({
  label,
  checked,
  onChange
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return <button onClick={onChange} style={{
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'transparent',
    border: 'none',
    padding: '11px 0',
    color: checked ? T.text : T.textSecondary,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'color .2s'
  }} data-id="element-1220" data-mp="33o8pyloazry">
      
      <span data-id="element-1221" data-mp="wl9kqpuromh8">{label}</span>
      <span style={{
      width: 40,
      height: 22,
      borderRadius: 99,
      padding: 2,
      background: checked ? 'linear-gradient(135deg,#FF8C00,#FF5A00)' : T.borderStrong,
      display: 'flex',
      justifyContent: checked ? 'flex-end' : 'flex-start',
      transition: 'all .2s',
      boxShadow: checked ? '0 2px 12px rgba(255,107,1,.5)' : 'none'
    }} data-id="element-1222" data-mp="10ozahin6ha3u">
        
        <span style={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: '#fff',
        boxShadow: '0 1px 6px rgba(0,0,0,.3)',
        transition: 'all .2s'
      }} data-id="element-1223" data-mp="cxk1j70wbujm" />
        
      </span>
    </button>;
}
// ── MODAL ─────────────────────────────────────────────────────────────────────
export function Modal({
  title,
  onClose,
  children
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} style={{
    position: 'fixed',
    inset: 0,
    background: 'rgba(255,255,255,.75)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 400,
    padding: 20
  }} data-id="element-1224" data-mp="3v3yeb2nfhygf">
      
      <motion.div initial={{
      opacity: 0,
      y: 16,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      duration: 0.26,
      ease: [0.2, 0.8, 0.2, 1]
    }} style={{
      background: 'rgba(255,255,255,0.97)',
      border: '1px solid rgba(0,0,0,.12)',
      borderRadius: 22,
      padding: 32,
      width: 460,
      maxWidth: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxShadow: '0 40px 100px rgba(0,0,0,.15), inset 0 1px 0 rgba(255,255,255,.6)'
    }} data-id="element-1225" data-mp="3pbkmb8oewsat">
        
        {/* Header */}
        <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 28,
        paddingBottom: 20,
        borderBottom: '1px solid rgba(0,0,0,.07)'
      }} data-id="element-1226" data-mp="14ktvmoyvcfzc">
          
          <span style={{
          fontSize: 18,
          fontWeight: 800,
          color: '#1A1207',
          letterSpacing: '-0.02em',
          fontFamily: "'Space Grotesk', sans-serif"
        }} data-id="element-1227" data-mp="2kb3593ekytep">
            
            {title}
          </span>
          <button onClick={onClose} style={{
          background: 'rgba(0,0,0,.04)',
          border: '1px solid rgba(0,0,0,.08)',
          borderRadius: 10,
          width: 34,
          height: 34,
          color: 'rgba(26,18,7,.5)',
          cursor: 'pointer',
          fontSize: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
          transition: 'background .15s'
        }} data-id="element-1228" data-mp="26bbl0kxh0gs9">
            
            ×
          </button>
        </div>
        {children}
      </motion.div>
    </motion.div>;
}
// ── PAGINATION ────────────────────────────────────────────────────────────────
// Hook reutilizable para paginar cualquier lista. Mantiene la página dentro de
// rango cuando cambian los datos o el tamaño de página.
export function usePagination<T>(items: T[], pageSize = 8) {
  const [page, setPage] = useState(1);
  // Defensa: si un caller pasa undefined (p. ej. datos aún sin cargar o una
  // forma de caché vieja), no rompemos el render al leer .length / .slice.
  const safeItems = Array.isArray(items) ? items : [];
  const total = safeItems.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => {
    setPage(p => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);
  // Reinicia a la primera página si la cantidad de items cambia drásticamente.
  useEffect(() => {
    setPage(1);
  }, [pageSize]);
  const start = (page - 1) * pageSize;
  const pageItems = safeItems.slice(start, start + pageSize);
  const rangeStart = total === 0 ? 0 : start + 1;
  const rangeEnd = Math.min(start + pageSize, total);
  return {
    page,
    setPage,
    totalPages,
    total,
    pageItems,
    rangeStart,
    rangeEnd,
    pageSize
  };
}
// Construye la lista de números de página con elipsis para rangos grandes.
function pageList(current: number, totalPages: number): (number | '…')[] {
  if (totalPages <= 7) return Array.from({
    length: totalPages
  }, (_, i) => i + 1);
  const out: (number | '…')[] = [1];
  const left = Math.max(2, current - 1);
  const right = Math.min(totalPages - 1, current + 1);
  if (left > 2) out.push('…');
  for (let p = left; p <= right; p++) out.push(p);
  if (right < totalPages - 1) out.push('…');
  out.push(totalPages);
  return out;
}
export function Pagination({
  page,
  totalPages,
  onChange,
  rangeStart,
  rangeEnd,
  total,
  label = 'registros'
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
  rangeStart?: number;
  rangeEnd?: number;
  total?: number;
  label?: string;
}) {
  const isMobile = useIsMobile();
  if (totalPages <= 1) return null;
  const numBtn = (active: boolean): CSSProperties => ({
    minWidth: 34,
    height: 34,
    padding: '0 10px',
    borderRadius: 10,
    border: `1px solid ${active ? T.accent : T.border}`,
    background: active ? T.accentSoft : 'rgba(255,255,255,.04)',
    color: active ? T.accent : T.textSecondary,
    fontSize: 13,
    fontWeight: active ? 800 : 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all .15s'
  });
  const arrow = (disabled: boolean): CSSProperties => ({
    width: 34,
    height: 34,
    borderRadius: 10,
    border: `1px solid ${T.border}`,
    background: 'rgba(255,255,255,.04)',
    color: disabled ? T.textMuted : T.textSecondary,
    fontSize: 16,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'inherit'
  });
  return <div style={{
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingTop: 14,
    flexWrap: 'wrap'
  }} data-id="element-1229" data-mp="3ei0clydcafvw">
      
      {typeof total === 'number' && <div style={{
      fontSize: 12,
      color: T.textMuted,
      fontWeight: 500,
      order: isMobile ? 2 : 0
    }} data-id="element-1230" data-mp="1ckq5jnl0s08c">
        
          {total === 0 ? `0 ${label}` : `${rangeStart}–${rangeEnd} de ${total} ${label}`}
        </div>}
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      order: isMobile ? 1 : 0
    }} data-id="element-1231" data-mp="1lkd4m2qa0ty8">
        
        <button type="button" aria-label="Página anterior" disabled={page <= 1} onClick={() => page > 1 && onChange(page - 1)} style={arrow(page <= 1)} data-id="element-1232" data-mp="1goxvfo37rm85">
          
          ‹
        </button>
        {pageList(page, totalPages).map((p, i) => p === '…' ? <span key={`e${i}`} style={{
        color: T.textMuted,
        padding: '0 4px',
        fontSize: 13
      }} data-id="element-1233" data-mp="151jqzji5794">
          
              …
            </span> : <button key={p} type="button" aria-current={p === page ? 'page' : undefined} onClick={() => onChange(p)} style={numBtn(p === page)} data-id="element-1234" data-mp="luvmpejynz0z">
          
              {p}
            </button>)}
        <button type="button" aria-label="Página siguiente" disabled={page >= totalPages} onClick={() => page < totalPages && onChange(page + 1)} style={arrow(page >= totalPages)} data-id="element-1235" data-mp="pyaef2h9i3u9">
          
          ›
        </button>
      </div>
    </div>;
}
// ── FIELD ─────────────────────────────────────────────────────────────────────
export function Field({
  label,
  children
}: {
  label: string;
  children: ReactNode;
}) {
  return <div style={{
    marginBottom: 18
  }} data-id="element-1236" data-mp="1dmbkm4qyx86i">
      
      <div style={{
      fontSize: 11,
      fontWeight: 700,
      color: '#1A1207',
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      marginBottom: 7
    }} data-id="element-1237" data-mp="1dmtnhk84ykne">
        
        {label}
      </div>
      {children}
    </div>;
}
// ─────────────────────────────────────────────────────────────────────────────
// CAMPOS VALIDADOS
//
// Un solo componente para todos los campos con formato. En vez de dejar
// escribir cualquier cosa y quejarse al guardar, filtra lo que no corresponde
// mientras se escribe, avisa por qué, y en los campos con formato conocido
// (placa, RNC, cédula, teléfono) va poniendo los guiones solo.
// ─────────────────────────────────────────────────────────────────────────────

export type ModoCampo = 'texto' | 'letras' | 'numeros' | 'decimal' | 'placa' | 'rnc' | 'cedula' | 'telefono' | 'codigo';

// Agrupa dígitos según un patrón: [1,2,5,1] sobre "131010001" da "1-31-01000-1".
function agrupar(digitos: string, grupos: number[], sep = '-'): string {
  const partes: string[] = [];
  let i = 0;
  for (const largo of grupos) {
    if (i >= digitos.length) break;
    partes.push(digitos.slice(i, i + largo));
    i += largo;
  }
  // Lo que sobre del último grupo se pega al final para no perder lo tecleado.
  if (i < digitos.length) partes.push(digitos.slice(i));
  return partes.join(sep);
}
type DefinicionCampo = {
  // Se prueba carácter por carácter sobre lo tecleado.
  permite: RegExp;
  // Qué se le dice al usuario cuando escribe algo que no corresponde.
  aviso: string;
  // Caracteres de formato que el propio campo pone: no cuentan como error
  // cuando vuelven a entrar al reescribir sobre el valor ya formateado.
  formato?: RegExp;
  transforma?: (limpio: string) => string;
  maximo?: number;
  teclado?: 'numeric' | 'tel';
};
const CAMPOS: Record<ModoCampo, DefinicionCampo> = {
  texto: {
    permite: /./,
    aviso: ''
  },
  // Se admiten espacios, tildes, apóstrofos y guiones porque los nombres
  // reales los llevan ("Sepúlveda", "D'Oleo", "María José").
  letras: {
    permite: /[\p{L}\s'.-]/u,
    aviso: 'Este campo solo admite letras'
  },
  numeros: {
    permite: /[0-9]/,
    aviso: 'Este campo solo admite números',
    teclado: 'numeric'
  },
  decimal: {
    permite: /[0-9.]/,
    aviso: 'Este campo solo admite números',
    teclado: 'numeric'
  },
  // Placa dominicana: letra(s) de serie y luego los dígitos, "L-317355".
  placa: {
    permite: /[A-Za-z0-9]/,
    aviso: 'La placa solo admite letras y números',
    formato: /-/,
    maximo: 12,
    transforma: v => {
      const s = v.toUpperCase();
      const letras = s.match(/^[A-Z]+/)?.[0] ?? '';
      const resto = s.slice(letras.length).replace(/[^0-9]/g, '');
      if (!letras) return resto;
      if (!resto) return letras;
      return `${letras}-${resto}`;
    }
  },
  // RNC: 9 dígitos agrupados 1-2-5-1.
  rnc: {
    permite: /[0-9]/,
    aviso: 'El RNC solo admite números',
    formato: /-/,
    maximo: 12,
    teclado: 'numeric',
    transforma: v => agrupar(v.slice(0, 9), [1, 2, 5, 1])
  },
  // Cédula: 11 dígitos agrupados 3-7-1.
  cedula: {
    permite: /[0-9]/,
    aviso: 'La cédula solo admite números',
    formato: /-/,
    maximo: 13,
    teclado: 'numeric',
    transforma: v => agrupar(v.slice(0, 11), [3, 7, 1])
  },
  // Teléfono dominicano: 10 dígitos, "(809) 247-2104".
  telefono: {
    permite: /[0-9]/,
    aviso: 'El teléfono solo admite números',
    formato: /[()\s-]/,
    maximo: 14,
    teclado: 'tel',
    transforma: v => {
      const d = v.slice(0, 10);
      if (d.length <= 3) return d;
      if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
      return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    }
  },
  // Códigos internos tipo "APP-87" o "33-21": no se les impone una posición
  // de guion porque cada empresa los numera distinto.
  codigo: {
    permite: /[A-Za-z0-9-]/,
    aviso: 'Este campo solo admite letras, números y guiones',
    transforma: v => v.toUpperCase()
  }
};
export function CampoValidado({
  modo = 'texto',
  valor,
  onChange,
  placeholder,
  style,
  maxLength,
  onEnter,
  autoFocus,
  disabled
}: {
  modo?: ModoCampo;
  valor: string;
  onChange: (v: string) => void;
  placeholder?: string;
  style?: CSSProperties;
  maxLength?: number;
  onEnter?: () => void;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  const def = CAMPOS[modo];
  const [aviso, setAviso] = useState('');

  // El aviso se borra solo: es una corrección momentánea, no un error
  // pendiente de resolver.
  useEffect(() => {
    if (!aviso) return;
    const t = setTimeout(() => setAviso(''), 2600);
    return () => clearTimeout(t);
  }, [aviso]);
  const manejar = (bruto: string) => {
    // Los caracteres de formato que pone el propio campo se quitan antes de
    // validar; si no, al reescribir sobre "L-317355" el guion contaría como
    // carácter inválido y avisaría sin razón.
    const sinFormato = def.formato ? bruto.replace(new RegExp(def.formato.source, 'g'), '') : bruto;
    const filtrado = Array.from(sinFormato).filter(c => def.permite.test(c)).join('');
    if (filtrado.length < sinFormato.length && def.aviso) setAviso(def.aviso);
    const final = def.transforma ? def.transforma(filtrado) : filtrado;
    const tope = maxLength ?? def.maximo;
    onChange(tope ? final.slice(0, tope) : final);
  };
  return <div data-id="element-1238" data-mp="1fc69gt2lvcxv">
      <input style={{
      ...inputStyle,
      ...(aviso ? {
        borderColor: 'rgba(220,38,38,.55)',
        background: 'rgba(220,38,38,.05)'
      } : null),
      ...style
    }} value={valor} disabled={disabled} autoFocus={autoFocus} inputMode={def.teclado} placeholder={placeholder} onChange={e => manejar(e.target.value)} onKeyDown={e => {
      if (e.key === 'Enter' && onEnter) onEnter();
    }} data-id="element-1239" data-mp="1fbwk97pdx9t8" />

      {aviso ? <div style={{
      marginTop: 5,
      fontSize: 11.5,
      fontWeight: 600,
      color: T.danger,
      display: 'flex',
      alignItems: 'center',
      gap: 5
    }} data-id="element-1240" data-mp="27zsmerxq3txe">

          <span style={{
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: T.dangerSoft,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        flexShrink: 0
      }} data-id="element-1241" data-mp="2gzp9ip4gskqc">

            !
          </span>
          {aviso}
        </div> : null}
    </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
// CONFIRMACIÓN
//
// Reemplaza a window.confirm, que no se puede estilizar, sale con el dominio
// del sitio arriba y se ve como un error del navegador en vez de una decisión
// del sistema.
//
// Se usa como un confirm normal, pero devolviendo una promesa:
//
//   const [dialogo, confirmar] = useConfirmacion();
//   ...
//   if (!(await confirmar({ titulo: '¿Anular la orden?', tono: 'danger' }))) return;
//   ...
//   return <>{dialogo} ...</>
// ─────────────────────────────────────────────────────────────────────────────

export type TonoConfirmacion = 'accent' | 'danger' | 'warn' | 'ok';
export interface OpcionesConfirmacion {
  titulo: string;
  mensaje?: string;
  // Datos concretos de lo que se va a hacer (orden, cantidad, destino...).
  // Se listan aparte para que se lean de un vistazo antes de decidir.
  detalles?: {
    etiqueta: string;
    valor: string;
  }[];
  // Consecuencia irreversible, si la hay.
  advertencia?: string;
  confirmar?: string;
  cancelar?: string;
  tono?: TonoConfirmacion;
  // Aviso en vez de pregunta: un solo botón, sin "Cancelar". Sirve para
  // reemplazar los alert() del navegador sin montar otro componente.
  soloAceptar?: boolean;
}
const TONOS: Record<TonoConfirmacion, {
  color: string;
  soft: string;
}> = {
  accent: {
    color: T.accent,
    soft: T.accentSoft
  },
  danger: {
    color: T.danger,
    soft: T.dangerSoft
  },
  warn: {
    color: T.warn,
    soft: T.warnSoft
  },
  ok: {
    color: T.ok,
    soft: T.okSoft
  }
};
export function useConfirmacion(): [ReactNode, (o: OpcionesConfirmacion) => Promise<boolean>] {
  const [pendiente, setPendiente] = useState<{
    opciones: OpcionesConfirmacion;
    resolver: (v: boolean) => void;
  } | null>(null);
  const isMobile = useIsMobile();
  const confirmar = (opciones: OpcionesConfirmacion) => new Promise<boolean>(resolver => setPendiente({
    opciones,
    resolver
  }));
  const cerrar = (respuesta: boolean) => {
    if (!pendiente) return;
    pendiente.resolver(respuesta);
    setPendiente(null);
  };

  // Escape cancela, igual que en el diálogo del navegador.
  useEffect(() => {
    if (!pendiente) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cerrar(false);
      if (e.key === 'Enter') cerrar(true);
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendiente]);
  const o = pendiente?.opciones;
  const tono = TONOS[o?.tono ?? 'accent'];
  const dialogo = pendiente ? <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} onClick={e => {
    if (e.target === e.currentTarget) cerrar(false);
  }} style={{
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    background: 'rgba(26,18,7,.42)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20
  }} data-id="element-1242" data-mp="1bsxqzjydxhwr">

      <motion.div initial={{
      opacity: 0,
      y: 14,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      duration: 0.18,
      ease: [0.2, 0.8, 0.2, 1]
    }} style={{
      width: '100%',
      maxWidth: 430,
      background: '#FFFCF8',
      borderRadius: 20,
      border: `1px solid ${T.border}`,
      boxShadow: '0 24px 60px rgba(0,0,0,.22)',
      overflow: 'hidden'
    }} data-id="element-1243" data-mp="3hu01l8ohz4sj">

        <div style={{
        padding: isMobile ? '24px 20px 0' : '28px 28px 0'
      }} data-id="element-1244" data-mp="2z38pw0h1f2az">
          <div style={{
          width: 46,
          height: 46,
          borderRadius: 14,
          background: tono.soft,
          border: `1px solid ${tono.color}33`,
          color: tono.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          fontWeight: 800,
          marginBottom: 16
        }} data-id="element-1245" data-mp="2bdo1v3611euu">

            {o?.soloAceptar ? '!' : '?'}
          </div>

          <div style={{
          fontSize: 18,
          fontWeight: 800,
          color: T.text,
          lineHeight: 1.3
        }} data-id="element-1246" data-mp="gxpoclqu8jbi">

            {o?.titulo}
          </div>

          {o?.mensaje ? <div style={{
          marginTop: 8,
          fontSize: 14,
          color: T.textSecondary,
          lineHeight: 1.5
        }} data-id="element-1247" data-mp="19ltxxicj5gjh">

              {o.mensaje}
            </div> : null}

          {o?.detalles?.length ? <div style={{
          marginTop: 16,
          border: `1px solid ${T.border}`,
          borderRadius: 12,
          overflow: 'hidden'
        }} data-id="element-1248" data-mp="3gdcisqbenmhs">

              {o.detalles.map((d, i) => <div key={d.etiqueta} style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 14,
            padding: '9px 13px',
            fontSize: 13,
            background: i % 2 ? 'transparent' : T.surface,
            borderTop: i ? `1px solid ${T.border}` : undefined
          }} data-id="element-1249" data-mp="fxmtrzydfr7j" data-mpi={i}>

                  <span style={{
              color: T.textMuted
            }} data-id="element-1250" data-mp="zl49uilx8s75">{d.etiqueta}</span>
                  <span style={{
              color: T.text,
              fontWeight: 700,
              textAlign: 'right'
            }} data-id="element-1251" data-mp="zkb49kyp88ql">

                    {d.valor}
                  </span>
                </div>)}
            </div> : null}

          {o?.advertencia ? <div style={{
          marginTop: 14,
          padding: '10px 13px',
          borderRadius: 10,
          background: T.warnSoft,
          border: `1px solid ${T.warn}33`,
          color: T.warn,
          fontSize: 12.5,
          fontWeight: 600,
          lineHeight: 1.45
        }} data-id="element-1252" data-mp="kudjlezza165">

              {o.advertencia}
            </div> : null}
        </div>

        <div style={{
        display: 'flex',
        gap: 10,
        justifyContent: 'flex-end',
        padding: isMobile ? 20 : '22px 28px 28px',
        flexDirection: isMobile ? 'column-reverse' : 'row'
      }} data-id="element-1253" data-mp="1uu7jzv1h2p03">

          {o?.soloAceptar ? null : <button type="button" onClick={() => cerrar(false)} style={{
          ...ghostButton,
          padding: '10px 20px'
        }} data-id="element-1254" data-mp="23umad8tamuag">

              {o?.cancelar ?? 'Cancelar'}
            </button>}
          <button type="button" autoFocus onClick={() => cerrar(true)} style={{
          ...primaryButton,
          background: tono.color,
          borderColor: tono.color,
          boxShadow: `0 4px 18px ${tono.color}55`
        }} data-id="element-1255" data-mp="2dn1ry50ohgut">

            {o?.confirmar ?? (o?.soloAceptar ? 'Entendido' : 'Confirmar')}
          </button>
        </div>
      </motion.div>
    </motion.div> : null;
  return [dialogo, confirmar];
}